import { getMovement } from '../config/movements';
import type { MovementId } from '../config/movements';
import type { RfidDocumentRow } from '../services/rfid.service';
import { useAuthStore } from '../stores/authStore';
import {
  recentDocumentScanId,
  useRecentScansStore,
  type RecentDocumentLineSnapshot,
  type RecentDocumentScan,
  type RecentDocumentScanStatus,
} from '../stores/recentScansStore';
import { useDocumentWizardStore } from '../stores/documentWizardStore';
import type { KitGroup, WizardLine } from '../types/wizard';
import {
  computeScanSessionStats,
  countAllocatedDocumentTags,
  getDisplayLineEntries,
} from './scanStats';
import type { KitBomItem } from './tagAllocation';
import { normalizeTagId } from './tagAllocation';
import type { DocketRow, DocketSection } from './successSummary';
import { getDocumentPlant } from './document';
import { countDocumentItems } from '../services/rfid.service';

function pushRow(rows: DocketRow[], label: string, value: unknown) {
  const v = String(value ?? '').trim();
  if (v) rows.push({ label, value: v });
}

function lineSnapshots(
  lines: WizardLine[],
  kitGroups: KitGroup[]
): RecentDocumentLineSnapshot[] {
  const entries = getDisplayLineEntries(lines, kitGroups);
  return entries.map(({ line, kitIndex }) => {
    const kitLabel =
      kitGroups.length > 0
        ? String(kitGroups[kitIndex]?.kitMaterial ?? kitGroups[kitIndex]?.kitDesc ?? '')
        : undefined;
    return {
      itemNo: line.itemNo,
      materialCode: line.materialCode,
      materialDesc: line.materialDesc,
      quantity: line.quantity,
      scanQty: line.scanQty,
      nonScanQty: line.nonScanQty ?? 0,
      scannable: line.scannable,
      fromBay: line.fromBay,
      fromBin: line.fromBin,
      rfidCodes: [...line.rfidCodes],
      kitLabel: kitLabel || undefined,
    };
  });
}

export function buildRecentDocumentScanFromWizard(
  extras?: {
    status?: RecentDocumentScanStatus;
    sapMessage?: string;
    sapGeneratedDocument?: string;
    sapMaterialDocument?: string;
  }
): RecentDocumentScan | null {
  const s = useDocumentWizardStore.getState();
  const { movementId, document, lines, kitGroups, kitBom, tagSet, variant } = s;
  if (!movementId || !document) return null;

  const docNo = String(document.DocumentNo ?? '').trim();
  if (!docNo) return null;

  const tagsScanned = countAllocatedDocumentTags(lines, kitGroups);
  const tagIds = Array.from(tagSet)
    .map(normalizeTagId)
    .filter(Boolean);

  if (tagsScanned === 0 && tagIds.length === 0) return null;

  const stats = computeScanSessionStats(lines, kitGroups, kitBom, tagSet);
  const movement = getMovement(movementId);
  const now = Date.now();
  const id = recentDocumentScanId(movementId, docNo);

  return {
    id,
    movementId,
    movementTitle: movement.title,
    variant,
    document: { ...document },
    scannedAt: now,
    updatedAt: now,
    status: extras?.status ?? 'scanning',
    tagsScanned: Math.max(tagsScanned, tagIds.length),
    linesComplete: stats.linesComplete,
    linesInProgress: stats.linesInProgress,
    totalRequired: stats.totalRequired,
    overallProgress: stats.overallProgress,
    tagIds,
    lines: lineSnapshots(lines, kitGroups),
    sapMessage: extras?.sapMessage,
    sapGeneratedDocument: extras?.sapGeneratedDocument,
    sapMaterialDocument: extras?.sapMaterialDocument,
  };
}

export function saveRecentDocumentScan(
  extras?: Parameters<typeof buildRecentDocumentScanFromWizard>[0]
): void {
  const userId = useAuthStore.getState().user?.userId;
  if (!userId) return;

  const entry = buildRecentDocumentScanFromWizard(extras);
  if (!entry) return;

  const existing = useRecentScansStore.getState().byUser[userId]?.find((e) => e.id === entry.id);
  if (existing) {
    entry.scannedAt = existing.scannedAt;
    if (extras?.status === 'scanning' && (existing.status === 'review' || existing.status === 'posted')) {
      entry.status = existing.status;
    }
  }

  useRecentScansStore.getState().upsert(userId, entry);
}

export function formatRecentScanTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function buildRecentDocumentScanSections(entry: RecentDocumentScan): DocketSection[] {
  const doc = entry.document;
  const plant = getDocumentPlant(doc);
  const itemCount = countDocumentItems(doc.Items);

  const documentRows: DocketRow[] = [];
  pushRow(documentRows, 'Movement', entry.movementTitle);
  pushRow(documentRows, 'SAP document', doc.DocumentNo);
  pushRow(documentRows, 'Customer', doc.CustName);
  pushRow(documentRows, 'Plant', plant);
  pushRow(documentRows, 'Sender', doc.SenderName ?? doc.Sender);
  pushRow(documentRows, 'Receiver', doc.ReceiverName ?? doc.Receiver);
  pushRow(documentRows, 'Posting date', doc.PostingDate);
  pushRow(documentRows, 'Document lines (SAP)', itemCount);
  pushRow(documentRows, 'Variant', entry.variant === 'kit' ? 'Kit' : 'Flat');

  const scanRows: DocketRow[] = [];
  pushRow(scanRows, 'Last updated', formatRecentScanTime(entry.updatedAt));
  pushRow(scanRows, 'First scanned', formatRecentScanTime(entry.scannedAt));
  pushRow(scanRows, 'Status', entry.status);
  pushRow(scanRows, 'RFID tags scanned', entry.tagsScanned);
  pushRow(scanRows, 'Scan progress', `${entry.overallProgress}%`);
  pushRow(scanRows, 'Lines complete', entry.linesComplete);
  pushRow(scanRows, 'Lines in progress', entry.linesInProgress);
  pushRow(scanRows, 'Total required qty', entry.totalRequired);

  const lineRows: DocketRow[] = entry.lines.map((line) => {
    const kit = line.kitLabel ? ` · Kit ${line.kitLabel}` : '';
    const tags =
      line.rfidCodes.length > 0 ? ` · Tags: ${line.rfidCodes.join(', ')}` : '';
    return {
      label: line.materialCode || line.itemNo,
      value: `${line.materialDesc}${kit} · Req ${line.quantity} · Scanned ${line.scanQty} + manual ${line.nonScanQty}${line.fromBay ? ` · Bay ${line.fromBay}` : ''}${tags}`,
    };
  });

  const tagRows: DocketRow[] =
    entry.tagIds.length > 0
      ? entry.tagIds.map((tag, i) => ({ label: `Tag ${i + 1}`, value: tag }))
      : [{ label: 'Tags', value: '—' }];

  const sections: DocketSection[] = [
    { title: 'SAP document', rows: documentRows },
    { title: 'Scan session', rows: scanRows },
    { title: 'Line items & tags', rows: lineRows },
    { title: 'All RFID tags', rows: tagRows },
  ];

  if (entry.sapMessage || entry.sapGeneratedDocument || entry.sapMaterialDocument) {
    const sapRows: DocketRow[] = [];
    pushRow(sapRows, 'SAP message', entry.sapMessage);
    pushRow(sapRows, 'Generated document', entry.sapGeneratedDocument);
    pushRow(sapRows, 'Material document', entry.sapMaterialDocument);
    sections.push({ title: 'SAP posting', rows: sapRows });
  }

  return sections;
}
